import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { BRAND, WHY_US } from "@/data/portfolio";
import { useSite } from "@/contexts/SiteContext";
import { fileUrl } from "@/lib/api";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); } }),
      { threshold: 0.08 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

export default function About() {
  useReveal();
  const { settings } = useSite() || {};
  const portrait = settings?.founderImage;
  const aboutText = settings?.aboutText ||
    "Mangalfera Films is a wedding photography and films brand based in Ahmedabad, led by Urvesh Thummar. With ten years of experience, 400+ weddings captured, and a team of 30+ professionals, we specialise in candid and cinematic wedding storytelling.";

  return (
    <div data-testid="about-page" className="bg-[#070707] text-[#F5EFE4]">

      {/* Hero */}
      <section className="pt-40 md:pt-52 pb-12 px-6 md:px-12" data-gsap="hero">
        <div className="max-w-[1380px] mx-auto">
          <span className="eyebrow text-[#B8914A] text-[0.6rem]" data-gsap="hero-kicker">About</span>
          <h1 className="page-hero-title mt-3" style={{ color: "#F5EFE4" }} data-gsap="hero-title">
            Stories woven<br />in{" "}
            <span className="font-display-italic text-[#B8914A]">light</span>.
          </h1>
        </div>
      </section>

      {/* Founder */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="about-founder" data-gsap="reveal">
        <div className="max-w-[1380px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-20 items-start">
          <div className="lg:col-span-4 reveal">
            <div className="frame bg-[#1a1a1a] gsap-image-hover" data-gsap="image" style={{ aspectRatio: "3/4" }}>
              {portrait && (
                <img src={fileUrl(portrait)} alt={BRAND.owner} loading="lazy" />
              )}
            </div>
            <div className="mt-5">
              <div className="font-display text-[1.6rem]">{BRAND.owner}</div>
              <div className="eyebrow text-[0.58rem] text-[#B8914A] mt-1">Founder · Lead Photographer</div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pt-6 reveal reveal-delay-1">
            <span className="gold-line block mb-7" />
            <p className="font-display text-[clamp(1.3rem,2.3vw,1.85rem)] leading-[1.6] text-[rgba(245,239,228,0.88)] whitespace-pre-line">
              {aboutText}
            </p>
            <p className="mt-7 text-[0.83rem] text-[rgba(245,239,228,0.55)] leading-[1.9]">
              For us, a wedding is not just an event. It is a celebration of emotions, families, traditions,
              rituals, and unforgettable moments. Our approach is simple: capture real emotions beautifully
              and create timeless memories that couples can relive forever.
            </p>
            <p className="mt-4 text-[0.83rem] text-[rgba(245,239,228,0.55)] leading-[1.9]">
              We shoot weddings across Ahmedabad, Gujarat, India, and destination wedding locations.
            </p>
            <Link to="/contact" className="btn-gold-ghost mt-10 inline-flex" data-testid="about-cta">
              Begin Your Story <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-28 px-6 md:px-12 border-t border-[rgba(184,145,74,0.14)]"
        data-testid="about-stats" data-gsap="reveal">
        <div className="max-w-[1380px] mx-auto">
          <div
            data-gsap="stagger"
            className="border border-[rgba(184,145,74,0.15)]"
            style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr) repeat(2,1fr)", gap: "1px", background: "rgba(184,145,74,0.1)" }}
          >
            {WHY_US.map((w, i) => (
              <div key={i} className="bg-[#070707] py-14 px-8 text-center md:text-left reveal"
                style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="stat-number">{w.num}</div>
                <div className="eyebrow text-[0.58rem] mt-3 text-[rgba(245,239,228,0.58)]">{w.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="py-24 md:py-36 px-6 bg-[#F5EFE4] text-[#111111]" data-gsap="reveal">
        <div className="max-w-4xl mx-auto text-center">
          <span className="eyebrow text-[#B8914A] text-[0.6rem]">Our Promise</span>
          <h2 className="font-display mt-4"
            style={{ fontSize: "clamp(2rem,4vw,3.8rem)", lineHeight: 1.1 }}>
            Every frame, a feeling.<br />Every film, a forever.
          </h2>
          <Link to="/contact" className="btn-dark-ghost mt-12 inline-flex" data-testid="about-final-cta">
            Get Custom Quotation <ArrowRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
